import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { 
  getIconComponent, 
  getBackgroundColor, 
  getIconGradient, 
  getTextColor,
  backgroundPattern
} from './AchievementUtils';
import { Achievement } from '../../types/achievement';

interface AchievementCardProps {
  achievement: Achievement;
  earned?: boolean;
  earnedDate?: Date;
  showEarnedDate?: boolean;
  onClick?: () => void;
}

/**
 * Reusable achievement card component used in both the trophy case and achievement dialogs
 */
const AchievementCard: React.FC<AchievementCardProps> = ({ 
  achievement, 
  earned = false,
  earnedDate,
  showEarnedDate = true,
  onClick
}) => {
  return (
    <Card 
      elevation={6} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.3s ease',
        borderRadius: 2,
        background: earned 
          ? 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)' 
          : 'linear-gradient(135deg, #f1f1f1 0%, #e5e5e5 100%)',
        border: earned ? '1px solid rgba(0,0,0,0.09)' : '1px solid rgba(0,0,0,0.05)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 12px 20px rgba(0,0,0,0.15)',
        },
        opacity: earned ? 1 : 0.85,
        filter: earned ? 'none' : 'grayscale(30%)'
      }}
      onClick={onClick}
    >
      {!earned && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            zIndex: 1
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              transform: 'scale(1.1)',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.2)'
              }
            }}
          >
            <Box sx={{ color: 'text.primary', fontSize: 30 }}>🔒</Box>
          </Box>
        </Box>
      )}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 2, 
          background: `radial-gradient(circle, rgba(255,255,255,1) 0%, ${getBackgroundColor(achievement.iconName)} 100%)`,
          opacity: earned ? 1 : 0.7,
          borderRadius: '16px 16px 0 0',
          height: 120,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {earned && (
          <>
            <Box sx={{ 
              position: 'absolute', 
              width: '100%', 
              height: '100%', 
              opacity: 0.12, 
              background: backgroundPattern,
              backgroundSize: 'cover'
            }} />
            <Box 
              sx={{
                position: 'absolute',
                background: getIconGradient(achievement.iconName),
                width: 200,
                height: 200,
                borderRadius: '50%'
              }}
            />
          </>
        )}
        <Box 
          sx={{ 
            transform: earned ? 'scale(1.2)' : 'scale(1)',
            transition: 'transform 0.3s ease',
            zIndex: 1,
            filter: !earned ? 'grayscale(40%)' : 'none'
          }}
        >
          {getIconComponent(achievement.iconName)}
        </Box>
        {earned && (
          <Box 
            component="span" 
            sx={{ 
              position: 'absolute',
              right: 16,
              top: 16,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: 'success.main',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>✓</span> EARNED
          </Box>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, pt: 1 }}>
        <Typography 
          gutterBottom 
          variant="h6" 
          component="div" 
          align="center"
          sx={{ 
            fontWeight: 600,
            color: earned ? getTextColor(achievement.iconName) : 'rgba(0, 0, 0, 0.6)'
          }}
        >
          {achievement.name}
        </Typography>
        <Divider sx={{ 
          my: 1,
          borderColor: earned ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.06)'
        }} />
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center"
          sx={{
            fontStyle: 'italic',
            fontSize: '0.85rem',
            lineHeight: 1.5
          }}
        >
          {achievement.description}
        </Typography>
      </CardContent>
      {earned && earnedDate && showEarnedDate && (
        <Box 
          sx={{ 
            p: 1.5, 
            background: 'linear-gradient(90deg, rgba(86,171,47,1) 0%, rgba(68,157,68,1) 100%)', 
            color: 'white', 
            textAlign: 'center',
            borderRadius: '0 0 8px 8px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.15' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1.5'/%3E%3Ccircle cx='13' cy='13' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.3
          }} />
          <Typography 
            variant="caption"
            sx={{
              fontWeight: 'bold',
              letterSpacing: 0.5,
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}
          >
            <span style={{ fontSize: '1rem' }}>🏆</span> Earned on {new Date(earnedDate).toLocaleDateString()}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default AchievementCard;